import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import { baseUrl } from "../../config/baseUrl";
import {
  fetchCategoryOptions,
  fetchPackages,
  getPackageLabel,
  getTargetLabel,
} from "./couponFormUtils";

export default function CouponEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [isFetchingPackages, setIsFetchingPackages] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [allPackages, setAllPackages] = useState([]);
  const [formval, setFormval] = useState({
    code: "",
    discountValue: "",
    validFrom: "",
    validTill: "",
    isActive: "true",
    category: "",
    forId: "",
    packageScope: "all",
    packageIds: [],
  });

  const requiresTargetSelection = ["library", "mentor", "ebook"].includes(
    formval.category
  );

  useEffect(() => {
    const fetchCouponDetails = async () => {
      try {
        setFetchLoading(true);
        const res = await axios.get(`${baseUrl}/coupons/${id}`, {
          headers: { Authorization: token },
        });

        if (res.status === 200) {
          const data = res.data.data;
          const category = data.category || "";
          const forId =
            data.libraryId?._id ||
            data.mentorId?._id ||
            data.ebookId?._id ||
            "";

          setFormval({
            code: data.code || "",
            discountValue: data.discountValue || "",
            validFrom: data.validFrom?.split("T")[0] || "",
            validTill: data.validTill?.split("T")[0] || "",
            isActive: String(data.isActive),
            category,
            forId,
            packageScope: data.applyToAllPackages === false ? "specific" : "all",
            packageIds: Array.isArray(data.packageIds)
              ? data.packageIds.map((pkg) => pkg?._id || pkg).filter(Boolean)
              : [],
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch coupon details");
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchCouponDetails();
    }
  }, [id, token]);

  useEffect(() => {
    const loadOptions = async () => {
      if (!formval.category || formval.category === "all") {
        setTypeOptions([]);
        setAllPackages([]);
        return;
      }

      if (requiresTargetSelection) {
        try {
          setIsFetchingOptions(true);
          const list = await fetchCategoryOptions(formval.category, token);
          setTypeOptions(list);
        } catch (error) {
          console.error(error);
          toast.error("Failed to load list for selected type");
          setTypeOptions([]);
        } finally {
          setIsFetchingOptions(false);
        }
      } else {
        setTypeOptions([]);
      }

      if (formval.category === "exam") {
        try {
          setIsFetchingPackages(true);
          const packages = await fetchPackages(token);
          setAllPackages(packages);
        } catch (error) {
          console.error(error);
          toast.error("Failed to load packages");
          setAllPackages([]);
        } finally {
          setIsFetchingPackages(false);
        }
      } else {
        setAllPackages([]);
      }
    };

    loadOptions();
  }, [formval.category, requiresTargetSelection, token]);

  const availablePackages = formval.category === "exam" ? allPackages : [];

  const packageOptions = availablePackages.map((pkg) => ({
    value: pkg._id,
    label: getPackageLabel(pkg),
  }));

  const selectedPackageOptions = packageOptions.filter((pkg) =>
    formval.packageIds.includes(pkg.value)
  );

  const changeHandler = (e) => {
    let { name, value } = e.target;

    if (name === "code") {
      value = value.toUpperCase();
    }

    if (name === "category") {
      setFormval((prev) => ({
        ...prev,
        category: value,
        forId: "",
        packageScope: "all",
        packageIds: [],
      }));
      return;
    }

    if (name === "forId") {
      setFormval((prev) => ({
        ...prev,
        forId: value,
        packageScope: "all",
        packageIds: [],
      }));
      return;
    }

    if (name === "packageScope") {
      setFormval((prev) => ({
        ...prev,
        packageScope: value,
        packageIds: value === "all" ? [] : prev.packageIds,
      }));
      return;
    }

    setFormval((prev) => ({ ...prev, [name]: value }));
  };

  const handlePackageChange = (selectedOptions) => {
    setFormval((prev) => ({
      ...prev,
      packageIds: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const validateForm = () => {
    const {
      code,
      discountValue,
      validFrom,
      validTill,
      category,
      forId,
      packageScope,
      packageIds,
    } = formval;

    if (!code.trim()) {
      toast.error("Coupon code is required");
      return false;
    }

    if (!discountValue || discountValue <= 0 || discountValue > 100) {
      toast.error("Discount must be between 1 and 100");
      return false;
    }

    if (!validFrom) {
      toast.error("Start date is required");
      return false;
    }

    if (!validTill) {
      toast.error("End date is required");
      return false;
    }

    if (new Date(validTill) <= new Date(validFrom)) {
      toast.error("End date must be after start date");
      return false;
    }

    if (requiresTargetSelection && !forId) {
      toast.error(`Please select a ${category} item`);
      return false;
    }

    if (category === "exam" && packageScope === "specific" && !packageIds.length) {
      toast.error("Please select at least one package");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      code: formval.code.trim(),
      discountType: "percentage",
      discountValue: Number(formval.discountValue),
      validFrom: formval.validFrom,
      validTill: formval.validTill,
      isActive: formval.isActive === "true",
      category: formval.category,
      applyToAllPackages: formval.packageScope === "all",
      packageIds: formval.packageScope === "specific" ? formval.packageIds : [],
    };

    switch (formval.category) {
      case "library":
        payload.libraryId = formval.forId;
        break;
      case "mentor":
        payload.mentorId = formval.forId;
        break;
      case "ebook":
        payload.ebookId = formval.forId;
        break;
      default:
        break;
    }

    try {
      setLoading(true);
      const res = await axios.patch(`${baseUrl}/coupons/${id}`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });

      if (res.status === 200) {
        toast.success("Coupon updated successfully");
        navigate("/coupons");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to update coupon");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <section className="main-sec">
        <div className="text-center py-5">
          <div className="spinner-border" role="status"></div>
          <p className="mt-2">Loading coupon details...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="main-sec">
      <div className="row align-items-center">
        <div className="col-lg-6">
          <div className="dashboard-title">
            <h4 className="dash-head">
              <i className="fa fa-edit me-2" />
              Edit Coupon
            </h4>
          </div>
          <div className="custom-bredcump">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/coupons">Coupons</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Edit Coupon
                </li>
              </ol>
            </nav>
          </div>
        </div>
        <div className="col-lg-6 d-flex justify-content-end">
          <Link to="/coupons" className="btn btn-info text-white">
            <i className="fa fa-arrow-left me-1" />
            Back
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="cards edit-usr">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Coupon Code<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="code"
                    value={formval.code}
                    onChange={changeHandler}
                    style={{ textTransform: "uppercase" }}
                    required
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Discount (%)<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="number"
                    name="discountValue"
                    className="form-control"
                    value={formval.discountValue}
                    onChange={changeHandler}
                    min="1"
                    max="100"
                    required
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Coupon Type<sup className="text-danger">*</sup>
                  </label>
                  <select
                    name="category"
                    className="form-control"
                    value={formval.category}
                    onChange={changeHandler}
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="all">All</option>
                    <option value="library">Library</option>
                    <option value="mentor">Mentor</option>
                    <option value="ebook">Ebook</option>
                    <option value="exam">Package</option>
                  </select>
                </div>

                {requiresTargetSelection && (
                  <div className="col-lg-6 mb-4">
                    <label className="form-label">
                      Select {formval.category}
                      <sup className="text-danger">*</sup>
                    </label>
                    <select
                      name="forId"
                      className="form-control"
                      value={formval.forId}
                      onChange={changeHandler}
                      required
                      disabled={isFetchingOptions}
                    >
                      <option value="">
                        {isFetchingOptions ? "Loading..." : "Choose one"}
                      </option>
                      {typeOptions.map((item) => (
                        <option key={item._id} value={item._id}>
                          {getTargetLabel(item, formval.category)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formval.category === "exam" && (
                  <>
                    <div className="col-lg-6 mb-4">
                      <label className="form-label">Package Scope</label>
                      <select
                        name="packageScope"
                        className="form-control"
                        value={formval.packageScope}
                        onChange={changeHandler}
                      >
                        <option value="all">All Packages</option>
                        <option value="specific">Specific Packages</option>
                      </select>
                      <small className="text-muted d-block mt-1">
                        Specific Packages option me aap single ya multiple package select kar sakte ho.
                      </small>
                    </div>

                    {formval.packageScope === "specific" && (
                      <div className="col-lg-6 mb-4">
                        <label className="form-label">
                          Select Packages<sup className="text-danger">*</sup>
                        </label>
                        <Select
                          isMulti
                          options={packageOptions}
                          value={selectedPackageOptions}
                          onChange={handlePackageChange}
                          isLoading={isFetchingPackages}
                          placeholder={
                            isFetchingPackages
                              ? "Loading packages..."
                              : "Select one or more packages"
                          }
                          noOptionsMessage={() => "No packages found"}
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Valid From<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="date"
                    name="validFrom"
                    className="form-control"
                    value={formval.validFrom}
                    onChange={changeHandler}
                    required
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label className="form-label">
                    Valid Till<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="date"
                    name="validTill"
                    className="form-control"
                    value={formval.validTill}
                    onChange={changeHandler}
                    min={formval.validFrom || ""}
                    required
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <label className="form-label">Status</label>
                  <select
                    name="isActive"
                    className="form-control"
                    value={formval.isActive}
                    onChange={changeHandler}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                <div className="col-lg-12 text-center">
                  <button type="submit" className="btn-save" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save me-1" />
                        Update Coupon
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <style>
        {`
          .btn-save {
            background: #008080;
            color: white;
            border: none;
            padding: 10px 30px;
            border-radius: 5px;
            font-weight: 500;
            transition: all 0.3s ease;
          }

          .btn-save:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
          }
        `}
      </style>
    </section>
  );
}
